import "./DashboardPage.css";
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faUtensils, faCalendarAlt, faImages, 
    faSignOutAlt, faBars, faTimes, faEdit,
    faTrash, faPlus, faSpinner, faCloudUploadAlt,
    faCalendarTimes
} from "@fortawesome/free-solid-svg-icons";
import { ApiService } from '../../services/api.service.js';
import { toast } from 'react-hot-toast';
import { API_URL } from '../../config/api.config';
import { getBackendImageUrl } from '../../utils/imageUtils';

function DashboardPage() {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('menu');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isAddGalleryModalOpen, setIsAddGalleryModalOpen] = useState(false);
    const [newGalleryItem, setNewGalleryItem] = useState({
        title: '',
        description: '',
        imageUrl: ''
    });

    const [menuItems, setMenuItems] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [filteredReservations, setFilteredReservations] = useState([]);
    const [galleryItems, setGalleryItems] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isAddMenuModalOpen, setIsAddMenuModalOpen] = useState(false);
    const [currentForm, setCurrentForm] = useState({
        title: '',
        path: '',
        imageUrl: '',
        isEditing: false,
        id: null
    });

    const [isEditMenuModalOpen, setIsEditMenuModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [dateFilter, setDateFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const reservationsPerPage = 5;

    const [isAddReservationModalOpen, setIsAddReservationModalOpen] = useState(false);
    const [isEditReservationModalOpen, setIsEditReservationModalOpen] = useState(false);
    const [currentReservation, setCurrentReservation] = useState({
        name: '',
        email: '',
        phone: '',
        reservationDate: '',
        reservationTime: '',
        numberOfPeople: '',
        specialRequests: ''
    });

    const [unavailableDates, setUnavailableDates] = useState([]);
    const [isAddUnavailableDateModalOpen, setIsAddUnavailableDateModalOpen] = useState(false);
    const [newUnavailableDate, setNewUnavailableDate] = useState({
        date: '',
        reason: ''
    });

    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        loadSectionData(activeSection);
    }, [activeSection, navigate]);

    useEffect(() => {
        if (reservations.length > 0) {
            let filtered = [...reservations];

            // Filtre par recherche (nom, email, téléphone)
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                filtered = filtered.filter(res => 
                    res.name.toLowerCase().includes(searchLower) ||
                    res.email.toLowerCase().includes(searchLower) ||
                    res.phone.toLowerCase().includes(searchLower)
                );
            }

            // Filtre par statut
            if (statusFilter !== 'ALL') {
                filtered = filtered.filter(res => res.status === statusFilter);
            }

            // Filtre par date
            if (dateFilter) {
                const filterDate = new Date(dateFilter).toDateString();
                filtered = filtered.filter(res => 
                    new Date(res.reservationDate).toDateString() === filterDate
                );
            }

            setFilteredReservations(filtered);
            setCurrentPage(1); // Réinitialiser la page courante lors du filtrage
        }
    }, [reservations, searchTerm, statusFilter, dateFilter]);

    // Calcul des réservations pour la page courante
    const indexOfLastReservation = currentPage * reservationsPerPage;
    const indexOfFirstReservation = indexOfLastReservation - reservationsPerPage;
    const currentReservations = filteredReservations.slice(indexOfFirstReservation, indexOfLastReservation);
    const totalPages = Math.ceil(filteredReservations.length / reservationsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const loadSectionData = async (section) => {
        setLoading(true);
        setError(null);
        try {
            switch (section) {
                case 'menu':
                    try {
                        const [mainMenu, annexMenus] = await Promise.all([
                            ApiService.getMainMenu(),
                            ApiService.getAnnexMenus()
                        ]);
                        const filteredAnnexMenus = annexMenus.filter(menu => menu.id !== mainMenu.id);
                        setMenuItems([mainMenu, ...filteredAnnexMenus]);
                    } catch (err) {
                        // Si le menu principal n'existe pas, on affiche uniquement les menus annexes
                        const annexMenus = await ApiService.getAnnexMenus();
                        setMenuItems(annexMenus);
                        setError("Aucun menu principal \"main\" n'a été défini. Veuillez en créer un.");
                    }
                    break;
                case 'reservations':
                    const reservationsData = await ApiService.getReservations();
                    setReservations(reservationsData);
                    break;
                case 'gallery':
                    const galleryData = await ApiService.getGalleryItems();
                    setGalleryItems(galleryData);
                    break;
                case 'unavailable-dates':
                    const allDates = await ApiService.getAllUnavailableDates();
                    setUnavailableDates(allDates);
                    break;
                default:
                    break;
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateReservation = async (id, status) => {
        try {
            const updatedReservation = await ApiService.updateReservationStatus(id, status);
            setReservations(prev => prev.map(res => 
                res.id === id ? updatedReservation : res
            ));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteGalleryItem = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) return;
        
        try {
            await ApiService.deleteGalleryItem(id);
            setGalleryItems(prev => prev.filter(item => item.id !== id));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleGalleryItemStatus = async (id) => {
        try {
            const updatedItem = await ApiService.toggleGalleryItemStatus(id);
            setGalleryItems(prev => prev.map(item => 
                item.id === id ? updatedItem : item
            ));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleAddGalleryItem = async (e) => {
        e.preventDefault();
        try {
            console.log('Soumission du formulaire avec les données:', newGalleryItem);
            const createdItem = await ApiService.createGalleryItem(newGalleryItem);
            console.log('Élément créé:', createdItem);
            setGalleryItems(prev => [...prev, createdItem]);
            setIsAddGalleryModalOpen(false);
            setNewGalleryItem({ title: '', description: '', imageUrl: '' });
        } catch (err) {
            console.error('Erreur lors de la création:', err);
            setError(err.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewGalleryItem(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            console.log('Fichier déposé:', file.name);
            await handleFileUpload(file);
        }
    }, []);

    const handleFileUpload = async (file) => {
        try {
            console.log('Début du téléchargement:', file.name);
            setUploadProgress(0);
            const data = await ApiService.uploadGalleryImage(file);
            console.log('Réponse du serveur:', data);
            setNewGalleryItem(prev => ({
                ...prev,
                imageUrl: data.imageUrl
            }));
            console.log('État mis à jour avec l\'URL:', data.imageUrl);
        } catch (err) {
            console.error('Erreur lors du téléchargement:', err);
            setError(err.message);
        } finally {
            setUploadProgress(0);
        }
    };

    const handleFileInput = async (e) => {
        const file = e.target.files[0];
        if (file) {
            console.log('Fichier sélectionné:', file.name);
            await handleFileUpload(file);
        }
    };

    const handleEditMenu = (menu) => {
        setCurrentForm({
            title: menu.title,
            path: menu.path,
            imageUrl: menu.path,
            isEditing: true,
            id: menu.id
        });
        setIsEditMenuModalOpen(true);
    };

    const handleMenuFileUpload = async (file, menuTitle) => {
        if (isUploading) return null;
        setIsUploading(true);
        
        try {
            console.log('Début du téléchargement du menu:', file.name);
            const uploadTitle = menuTitle && menuTitle.trim().toLowerCase() === 'main' 
                ? `main-menu-${Date.now()}` 
                : menuTitle ? menuTitle.trim() : `temp-${Date.now()}`;
            
            const data = await ApiService.uploadMenuImage(file, uploadTitle);
            console.log('Réponse du serveur pour le menu:', data);
            return data.imageUrl;
        } catch (err) {
            console.error('Erreur lors du téléchargement du menu:', err);
            setError(err.message);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    const handleMenuFileInput = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.target.files[0];
        if (!file || isUploading) return;

        console.log('Fichier menu sélectionné:', file.name);
        const imageUrl = await handleMenuFileUpload(file, currentForm.title);
        
        if (imageUrl) {
            setCurrentForm(prev => ({ ...prev, imageUrl }));
        }
    };

    const handleMenuDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const file = e.dataTransfer.files[0];
        if (!file || !file.type.startsWith('image/') || isUploading) return;

        console.log('Fichier menu déposé:', file.name);
        const imageUrl = await handleMenuFileUpload(file, currentForm.title);
        
        if (imageUrl) {
            setCurrentForm(prev => ({ ...prev, imageUrl }));
        }
    };

    const handleAddMenu = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isUploading) return;

        try {
            const isMainMenu = currentForm.title.trim().toLowerCase() === 'main';
            const menuToCreate = {
                ...currentForm,
                isMainMenu,
                path: currentForm.imageUrl,
                title: currentForm.title.trim()
            };
            
            const createdMenu = await ApiService.createMenuItem(menuToCreate);
            setMenuItems(prev => [...prev, createdMenu]);
            setIsAddMenuModalOpen(false);
            setCurrentForm({ title: '', path: '', imageUrl: '', isEditing: false, id: null });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleUpdateMenu = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isUploading) return;

        try {
            const isMainMenu = currentForm.title.trim().toLowerCase() === 'main';
            const menuToUpdate = {
                ...currentForm,
                isMainMenu,
                path: currentForm.imageUrl || currentForm.path,
                title: currentForm.title.trim()
            };
            
            const updatedMenu = await ApiService.updateMenuItem(currentForm.id, menuToUpdate);
            setMenuItems(prev => prev.map(menu => 
                menu.id === currentForm.id ? updatedMenu : menu
            ));
            setIsEditMenuModalOpen(false);
            setCurrentForm({ title: '', path: '', imageUrl: '', isEditing: false, id: null });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteMenu = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce menu ?')) return;
        
        try {
            await ApiService.deleteMenuItem(id);
            const [mainMenu, annexMenus] = await Promise.all([
                ApiService.getMainMenu(),
                ApiService.getAnnexMenus()
            ]);
            const filteredAnnexMenus = annexMenus.filter(menu => menu.id !== mainMenu.id);
            setMenuItems([mainMenu, ...filteredAnnexMenus]);
        } catch (err) {
            console.error('Erreur lors de la suppression:', err);
            setError(err.message);
        }
    };

    const handleDeleteReservation = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) return;
        
        try {
            await ApiService.deleteReservation(id);
            setReservations(prev => prev.filter(res => res.id !== id));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAddReservation = async (e) => {
        e.preventDefault();
        try {
            // Combiner la date et l'heure
            const dateTime = new Date(`${currentReservation.reservationDate}T${currentReservation.reservationTime}`);
            
            const reservationData = {
                ...currentReservation,
                reservationDate: dateTime.toISOString(),
                numberOfPeople: parseInt(currentReservation.numberOfPeople)
            };

            const createdReservation = await ApiService.createReservation(reservationData);
            setReservations(prev => [...prev, createdReservation]);
            setIsAddReservationModalOpen(false);
            setCurrentReservation({
                name: '',
                email: '',
                phone: '',
                reservationDate: '',
                reservationTime: '',
                numberOfPeople: '',
                specialRequests: ''
            });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEditReservation = async (e) => {
        e.preventDefault();
        try {
            // Combiner la date et l'heure
            const dateTime = new Date(`${currentReservation.reservationDate}T${currentReservation.reservationTime}`);
            
            const reservationData = {
                ...currentReservation,
                reservationDate: dateTime.toISOString(),
                numberOfPeople: parseInt(currentReservation.numberOfPeople)
            };

            const updatedReservation = await ApiService.updateReservation(currentReservation.id, reservationData);
            setReservations(prev => prev.map(res => 
                res.id === currentReservation.id ? updatedReservation : res
            ));
            setIsEditReservationModalOpen(false);
            setCurrentReservation({
                name: '',
                email: '',
                phone: '',
                reservationDate: '',
                reservationTime: '',
                numberOfPeople: '',
                specialRequests: ''
            });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleReservationInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentReservation(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddUnavailableDate = async (e) => {
        e.preventDefault();
        try {
            const createdDate = await ApiService.addUnavailableDate({
                date: newUnavailableDate.date,
                reason: newUnavailableDate.reason
            });
            
            setUnavailableDates(prev => [...prev, createdDate]);
            setIsAddUnavailableDateModalOpen(false);
            setNewUnavailableDate({ date: '', reason: '' });
            toast.success('Date non disponible ajoutée avec succès');
        } catch (err) {
            console.error('Erreur:', err);
            toast.error(err.message || 'Une erreur est survenue lors de l\'ajout de la date non disponible');
        }
    };

    const handleDeleteUnavailableDate = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette date non disponible ?')) return;
        try {
            await ApiService.removeUnavailableDate(id);
            setUnavailableDates(prev => prev.filter(d => d.id !== id));
        } catch (err) {
            setError(err.message);
        }
    };

    const sortedUnavailableDates = [...unavailableDates].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    const renderMenuSection = () => (
        <div className="dashboard-section">
            <div className="section-header">
                <button 
                    className="add-button"
                    onClick={() => setIsAddMenuModalOpen(true)}
                >
                    <FontAwesomeIcon icon={faPlus} /> Ajouter un Menu
                </button>
            </div>

            {loading ? (
                <div className="loading">Chargement...</div>
            ) : (
                <>
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}
                    <div className="menu-list">
                        {menuItems.map(menu => (
                            <div key={menu.id} className="menu-item">
                                <div className="menu-info">
                                    <img src={getBackendImageUrl(menu.path)} alt={menu.title} className="menu-image" />
                                    <h3>{menu.title}</h3>
                                    <p>Chemin: {menu.path}</p>
                                    <span className={`menu-type ${menu.title === 'main' ? 'main' : 'annex'}`}>
                                        {menu.title === 'main' ? 'Menu Principal' : 'Menu Annexe'}
                                    </span>
                                </div>
                                <div className="menu-actions">
                                    <button onClick={() => handleEditMenu(menu)}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button onClick={() => handleDeleteMenu(menu.id)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {isAddMenuModalOpen && (
                <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Ajouter un Menu</h2>
                            <button 
                                className="close-modal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsAddMenuModalOpen(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleAddMenu} className="gallery-form" onClick={(e) => e.stopPropagation()}>
                            <div className="form-group">
                                <label htmlFor="title">Titre</label>
                                <input
                                    type="text"
                                    id="title"
                                    value={currentForm.title}
                                    onChange={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCurrentForm(prev => ({ ...prev, title: e.target.value }));
                                    }}
                                    required
                                />
                            </div>
                            <div 
                                className={`drop-zone ${isDragging ? 'dragging' : ''} ${currentForm.imageUrl ? 'has-image' : ''}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsDragging(true);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsDragging(false);
                                }}
                                onDrop={handleMenuDrop}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {currentForm.imageUrl ? (
                                    <div className="preview-container">
                                        <img src={getBackendImageUrl(currentForm.imageUrl)} alt="Preview" />
                                        <button 
                                            type="button" 
                                            className="remove-image"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setCurrentForm(prev => ({ ...prev, imageUrl: '' }));
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCloudUploadAlt} className="upload-icon" />
                                        <p>Glissez-déposez votre image ici</p>
                                        <p>ou</p>
                                        <label className="upload-button">
                                            Parcourir
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleMenuFileInput}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </>
                                )}
                            </div>
                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsAddMenuModalOpen(false);
                                    }}
                                >
                                    Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    className="submit-button"
                                    disabled={!currentForm.imageUrl}
                                >
                                    Ajouter
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isEditMenuModalOpen && (
                <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Modifier le Menu</h2>
                            <button 
                                className="close-modal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsEditMenuModalOpen(false);
                                    setCurrentForm({ title: '', path: '', imageUrl: '', isEditing: false, id: null });
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateMenu} className="gallery-form" onClick={(e) => e.stopPropagation()}>
                            <div className="form-group">
                                <label htmlFor="edit-title">Titre</label>
                                <input
                                    type="text"
                                    id="edit-title"
                                    value={currentForm.title}
                                    onChange={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCurrentForm(prev => ({ ...prev, title: e.target.value }));
                                    }}
                                    required
                                    disabled={isUploading}
                                />
                            </div>
                            <div 
                                className={`drop-zone ${isDragging ? 'dragging' : ''} ${currentForm.imageUrl ? 'has-image' : ''}`}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isUploading) setIsDragging(true);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsDragging(false);
                                }}
                                onDrop={handleMenuDrop}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {currentForm.imageUrl ? (
                                    <div className="preview-container">
                                        <img src={getBackendImageUrl(currentForm.imageUrl)} alt="Preview" />
                                        <button 
                                            type="button" 
                                            className="remove-image"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!isUploading) {
                                                    setCurrentForm(prev => ({ ...prev, imageUrl: prev.path }));
                                                }
                                            }}
                                            disabled={isUploading}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCloudUploadAlt} className="upload-icon" />
                                        <p>Glissez-déposez votre image ici</p>
                                        <p>ou</p>
                                        <label className="upload-button">
                                            Parcourir
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleMenuFileInput}
                                                style={{ display: 'none' }}
                                                disabled={isUploading}
                                            />
                                        </label>
                                    </>
                                )}
                                {isUploading && (
                                    <div className="upload-progress">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <p>Upload en cours...</p>
                                    </div>
                                )}
                            </div>
                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsEditMenuModalOpen(false);
                                        setCurrentForm({ title: '', path: '', imageUrl: '', isEditing: false, id: null });
                                    }}
                                    disabled={isUploading}
                                >
                                    Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    className="submit-button"
                                    disabled={isUploading}
                                >
                                    Mettre à jour
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );

    const renderReservationsSection = () => (
        <div className="reservations-section">
            <div className="section-header">
                <button 
                    className="add-button"
                    onClick={() => setIsAddReservationModalOpen(true)}
                >
                    <FontAwesomeIcon icon={faPlus} /> Ajouter une Réservation
                </button>
            </div>

            <div className="reservations-filters">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Rechercher par nom, email ou téléphone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filters">
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="ALL">Tous les statuts</option>
                        <option value="PENDING">En attente</option>
                        <option value="CONFIRMED">Confirmées</option>
                        <option value="CANCELLED">Annulées</option>
                    </select>
                    <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                    />
                    {dateFilter && (
                        <button 
                            className="clear-date"
                            onClick={() => setDateFilter('')}
                        >
                            Effacer la date
                        </button>
                    )}
                </div>
            </div>

            <div className="reservations-list">
                {currentReservations.map(reservation => (
                    <div key={reservation.id} className="reservation-item">
                        <div className="reservation-info">
                            <h3>{reservation.name}</h3>
                            <p><strong>Email:</strong> {reservation.email}</p>
                            <p><strong>Téléphone:</strong> {reservation.phone}</p>
                            <p><strong>Date:</strong> {new Date(reservation.reservationDate).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            })}</p>
                            <p><strong>Heure:</strong> {new Date(reservation.reservationDate).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}</p>
                            <p><strong>Nombre de personnes:</strong> {reservation.numberOfPeople}</p>
                            {reservation.specialRequests && (
                                <p><strong>Message spécial:</strong> {reservation.specialRequests}</p>
                            )}
                            <p className={`status ${reservation.status.toLowerCase()}`}>
                                <strong>Statut:</strong> {reservation.status === 'PENDING' ? 'En attente' : 
                                       reservation.status === 'CONFIRMED' ? 'Confirmée' : 
                                       'Annulée'}
                            </p>
                        </div>
                        <div className="reservation-actions">
                            <button 
                                className="edit-button"
                                onClick={() => {
                                    setCurrentReservation({
                                        ...reservation,
                                        reservationDate: new Date(reservation.reservationDate).toISOString().split('T')[0],
                                        reservationTime: new Date(reservation.reservationDate).toLocaleTimeString('fr-FR', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })
                                    });
                                    setIsEditReservationModalOpen(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </button>
                            {reservation.status === 'PENDING' && (
                                <>
                                    <button 
                                        className="confirm-button"
                                        onClick={() => handleUpdateReservation(reservation.id, 'CONFIRMED')}
                                    >
                                        Confirmer
                                    </button>
                                    <button 
                                        className="cancel-button"
                                        onClick={() => handleUpdateReservation(reservation.id, 'CANCELLED')}
                                    >
                                        Annuler
                                    </button>
                                </>
                            )}
                            <button 
                                className="delete-button"
                                onClick={() => handleDeleteReservation(reservation.id)}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Précédent
                    </button>
                    <span>Page {currentPage} sur {totalPages}</span>
                    <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Suivant
                    </button>
                </div>
            )}

            {/* Modal d'ajout de réservation */}
            {isAddReservationModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Ajouter une Réservation</h2>
                            <button 
                                className="close-modal"
                                onClick={() => setIsAddReservationModalOpen(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleAddReservation} className="reservation-form">
                            <div className="form-group">
                                <label htmlFor="name">Nom</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={currentReservation.name}
                                    onChange={handleReservationInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={currentReservation.email}
                                    onChange={handleReservationInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone">Téléphone</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={currentReservation.phone}
                                    onChange={handleReservationInputChange}
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="reservationDate">Date</label>
                                    <input
                                        type="date"
                                        id="reservationDate"
                                        name="reservationDate"
                                        value={currentReservation.reservationDate}
                                        onChange={handleReservationInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="reservationTime">Heure</label>
                                    <input
                                        type="time"
                                        id="reservationTime"
                                        name="reservationTime"
                                        value={currentReservation.reservationTime}
                                        onChange={handleReservationInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="numberOfPeople">Nombre de personnes</label>
                                <select
                                    id="numberOfPeople"
                                    name="numberOfPeople"
                                    value={currentReservation.numberOfPeople}
                                    onChange={handleReservationInputChange}
                                    required
                                >
                                    <option value="">Sélectionner</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                        <option key={num} value={num}>
                                            {num} {num === 1 ? 'personne' : 'personnes'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="specialRequests">Message spécial</label>
                                <textarea
                                    id="specialRequests"
                                    name="specialRequests"
                                    value={currentReservation.specialRequests}
                                    onChange={handleReservationInputChange}
                                    rows="4"
                                />
                            </div>
                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={() => setIsAddReservationModalOpen(false)}
                                >
                                    Annuler
                                </button>
                                <button type="submit" className="submit-button">
                                    Ajouter
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de modification de réservation */}
            {isEditReservationModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Modifier la Réservation</h2>
                            <button 
                                className="close-modal"
                                onClick={() => setIsEditReservationModalOpen(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleEditReservation} className="reservation-form">
                            <div className="form-group">
                                <label htmlFor="edit-name">Nom</label>
                                <input
                                    type="text"
                                    id="edit-name"
                                    name="name"
                                    value={currentReservation.name}
                                    onChange={handleReservationInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-email">Email</label>
                                <input
                                    type="email"
                                    id="edit-email"
                                    name="email"
                                    value={currentReservation.email}
                                    onChange={handleReservationInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-phone">Téléphone</label>
                                <input
                                    type="tel"
                                    id="edit-phone"
                                    name="phone"
                                    value={currentReservation.phone}
                                    onChange={handleReservationInputChange}
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="edit-reservationDate">Date</label>
                                    <input
                                        type="date"
                                        id="edit-reservationDate"
                                        name="reservationDate"
                                        value={currentReservation.reservationDate}
                                        onChange={handleReservationInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="edit-reservationTime">Heure</label>
                                    <input
                                        type="time"
                                        id="edit-reservationTime"
                                        name="reservationTime"
                                        value={currentReservation.reservationTime}
                                        onChange={handleReservationInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-numberOfPeople">Nombre de personnes</label>
                                <select
                                    id="edit-numberOfPeople"
                                    name="numberOfPeople"
                                    value={currentReservation.numberOfPeople}
                                    onChange={handleReservationInputChange}
                                    required
                                >
                                    <option value="">Sélectionner</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                        <option key={num} value={num}>
                                            {num} {num === 1 ? 'personne' : 'personnes'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-specialRequests">Message spécial</label>
                                <textarea
                                    id="edit-specialRequests"
                                    name="specialRequests"
                                    value={currentReservation.specialRequests}
                                    onChange={handleReservationInputChange}
                                    rows="4"
                                />
                            </div>
                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={() => setIsEditReservationModalOpen(false)}
                                >
                                    Annuler
                                </button>
                                <button type="submit" className="submit-button">
                                    Mettre à jour
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );

    const renderUnavailableDatesSection = () => (
        <div className="unavailable-dates-section">
            <div className="section-header">
                <button 
                    className="add-button"
                    onClick={() => setIsAddUnavailableDateModalOpen(true)}
                >
                    <FontAwesomeIcon icon={faPlus} /> Ajouter une date non disponible
                </button>
                <button
                    className="sort-button"
                    style={{ marginLeft: 16 }}
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                    Trier {sortOrder === 'asc' ? '↓' : '↑'}
                </button>
            </div>

            <div className="unavailable-dates-list">
                {sortedUnavailableDates.map((item, index) => (
                    <div key={item.id || index} className="unavailable-date-item">
                        <div className="date-info">
                            <h3>{new Date(item.date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}</h3>
                            {item.reason && <p style={{ fontStyle: 'italic', color: '#888' }}>{item.reason}</p>}
                        </div>
                        <div className="date-actions">
                            <button 
                                className="delete-button"
                                onClick={() => handleDeleteUnavailableDate(item.id)}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {isAddUnavailableDateModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Ajouter une date non disponible</h2>
                            <button 
                                className="close-modal"
                                onClick={() => setIsAddUnavailableDateModalOpen(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleAddUnavailableDate} className="unavailable-date-form">
                            <div className="form-group">
                                <label htmlFor="date">Date</label>
                                <input
                                    type="date"
                                    id="date"
                                    value={newUnavailableDate.date}
                                    onChange={(e) => setNewUnavailableDate(prev => ({ ...prev, date: e.target.value }))}
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="reason">Raison (optionnelle)</label>
                                <textarea
                                    id="reason"
                                    value={newUnavailableDate.reason}
                                    onChange={(e) => setNewUnavailableDate(prev => ({ ...prev, reason: e.target.value }))}
                                    rows="4"
                                />
                            </div>
                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={() => setIsAddUnavailableDateModalOpen(false)}
                                >
                                    Annuler
                                </button>
                                <button type="submit" className="submit-button">
                                    Ajouter
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="loading-screen">
                <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                <p>Chargement...</p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="sidebar-header">
                    <img src="/images/logo.png" alt="Logo" className="dashboard-logo" />
                    <button className="toggle-sidebar" onClick={toggleSidebar}>
                        <FontAwesomeIcon icon={isSidebarOpen ? faTimes : faBars} />
                    </button>
                </div>

                <nav className="sidebar-nav">
                    <button 
                        className={activeSection === 'menu' ? 'active' : ''} 
                        onClick={() => setActiveSection('menu')}
                    >
                        <FontAwesomeIcon icon={faUtensils} />
                        <span>Menu</span>
                    </button>
                    <button 
                        className={activeSection === 'reservations' ? 'active' : ''} 
                        onClick={() => setActiveSection('reservations')}
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Réservations</span>
                    </button>
                    <button 
                        className={activeSection === 'unavailable-dates' ? 'active' : ''} 
                        onClick={() => setActiveSection('unavailable-dates')}
                    >
                        <FontAwesomeIcon icon={faCalendarTimes} />
                        <span>Dates non disponibles</span>
                    </button>
                    <button 
                        className={activeSection === 'gallery' ? 'active' : ''} 
                        onClick={() => setActiveSection('gallery')}
                    >
                        <FontAwesomeIcon icon={faImages} />
                        <span>Galerie</span>
                    </button>
                </nav>

                <button className="logout-button" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span>Déconnexion</span>
                </button>
            </aside>

            <main className="dashboard-main">
                <div className="dashboard-header">
                    <h1>
                        {activeSection === 'menu' && 'Gestion du Menu'}
                        {activeSection === 'reservations' && 'Gestion des Réservations'}
                        {activeSection === 'unavailable-dates' && 'Gestion des Dates non disponibles'}
                        {activeSection === 'gallery' && 'Gestion de la Galerie'}
                    </h1>
                </div>

                <div className="dashboard-content">
                    {activeSection === 'menu' && renderMenuSection()}
                    {activeSection === 'reservations' && renderReservationsSection()}
                    {activeSection === 'unavailable-dates' && renderUnavailableDatesSection()}
                    {activeSection === 'gallery' && (
                        <div className="gallery-section">
                            <button 
                                className="add-button"
                                onClick={() => setIsAddGalleryModalOpen(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Ajouter une image
                            </button>
                            <div className="gallery-grid">
                                {galleryItems.map(item => (
                                    <div key={item.id} className="gallery-item">
                                        <img src={getBackendImageUrl(item.imageUrl)} alt={item.title} />
                                        <div className="gallery-item-overlay">
                                            <h3>{item.title}</h3>
                                            <p>{item.description}</p>
                                            <div className="gallery-item-actions">
                                                <button 
                                                    className="toggle-button"
                                                    onClick={() => handleToggleGalleryItemStatus(item.id)}
                                                >
                                                    {item.active ? 'Désactiver' : 'Activer'}
                                                </button>
                                                <button 
                                                    className="delete-button"
                                                    onClick={() => handleDeleteGalleryItem(item.id)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Modal d'ajout d'image */}
            {isAddGalleryModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Ajouter une image</h2>
                            <button 
                                className="close-modal"
                                onClick={() => setIsAddGalleryModalOpen(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleAddGalleryItem} className="gallery-form">
                            <div className="form-group">
                                <label htmlFor="title">Titre</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={newGalleryItem.title}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={newGalleryItem.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div 
                                className={`drop-zone ${isDragging ? 'dragging' : ''} ${newGalleryItem.imageUrl ? 'has-image' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                {newGalleryItem.imageUrl ? (
                                    <div className="preview-container">
                                        <img src={getBackendImageUrl(newGalleryItem.imageUrl)} alt="Preview" />
                                        <button 
                                            type="button" 
                                            className="remove-image"
                                            onClick={() => setNewGalleryItem(prev => ({ ...prev, imageUrl: '' }))}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCloudUploadAlt} className="upload-icon" />
                                        <p>Glissez-déposez votre image ici</p>
                                        <p>ou</p>
                                        <label className="upload-button">
                                            Parcourir
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileInput}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </>
                                )}
                                {uploadProgress > 0 && (
                                    <div className="upload-progress">
                                        <div 
                                            className="progress-bar"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setIsAddGalleryModalOpen(false)}>
                                    Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    className="submit-button"
                                    disabled={!newGalleryItem.imageUrl}
                                >
                                    Ajouter
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DashboardPage; 